/**
 * AiConfigService — client for the assistant AI configuration of one
 * {@link AssistantScope} (`/networks/{id}/config` or `/frontdoors/{id}/config`), served by the standalone `ai-assistant` service off
 * `environment.aiAssistantUrl`. Note the resource is `config`, not `ai-config`:
 * in a service that is only about the assistant there is nothing else a
 * network's config could mean.
 *
 * The AI config is a singleton sub-resource of a Network (1:1), not a
 * collection, so this service exposes the resource's verbs directly. The bearer
 * token is attached by the Auth0 HTTP interceptor, same as every other
 * HttpClient call.
 *
 * Contract (from the backend `ZitiConfigController` and `FrontdoorConfigController`):
 *  - GET                  → current AiConfigView (all-null == service defaults)
 *  - PUT                  → apply UpdateAiConfig; each part applied only when
 *                           present, so enabled / model / key / mcpClients are
 *                           independent
 *  - DELETE               → reset to service defaults (clears model AND key),
 *                           but a network switched off STAYS off — only a PUT
 *                           with `assistantEnabled: true` switches it back on
 *  - GET /allowed-models  → model ids the network may pick, grouped by backend
 *  - GET /allowed-mcp-clients → configured outbound MCP client server names
 *
 * Reads need `read` on the network or account; writes need `update`.
 */

import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, signal } from '@angular/core';
import { MonoTypeOperatorFunction, Observable, tap } from 'rxjs';
import { ENVIRONMENT, Environment } from '@netfoundry-ui/shared/model';
import { HTTP_CLIENT } from '@netfoundry-ui/shared/services';
import { AssistantScope, assistantScopeKey, assistantScopeUrl } from './assistant-scope';

/** HAL `_links` as returned alongside every view in this service. */
export type HalLinks = Record<string, { href: string; templated?: boolean }>;

/**
 * A selectable model — the `model` identifier passed to the backend and its
 * human-readable `label`. The backend supplies the label (falling back to the
 * identifier when the operator config omits one), so the console never has to
 * derive display names for models itself.
 */
export interface ModelOption {
    model: string;
    label: string;
}

/**
 * One backend group in the allowed-models listing: the backend `backend` key
 * (the value used on the API and in the DB, e.g. `azure-openai`), its
 * human-readable `label`, the `defaultBaseUrl` to pre-fill (null when the
 * backend has no default — Azure/Ollama — or doesn't use one — Vertex), the
 * `supported` and `required` field paths, and the `models` selectable for it.
 *
 * `supported` is every field path the backend uses (required first, then
 * optional); `required` is the mandatory subset. A field absent from
 * `supported` is NOT applicable to this backend and must not be shown (e.g.
 * `baseUrl` for vertex-gemini, `apiKey` for ollama).
 */
export interface BackendModelsView {
    backend: string;
    label: string;
    defaultBaseUrl: string | null;
    supported: string[];
    required: string[];
    models: ModelOption[];
}

/**
 * An outbound MCP client server as surfaced in a response: its connection
 * `key` (the identifier used in the `mcpClients` selection), a human-readable
 * `name`, and whether it is `enabled`. In an `AiConfigView.mcpClients` entry
 * the flag is the network's current state; in the `allowed-mcp-clients`
 * catalog it is the default (all servers enabled).
 */
export interface McpClientView {
    key: string;
    name: string;
    enabled: boolean;
}

/**
 * A network's assistant AI configuration — response body of GET/PUT
 * `/networks/{id}/config` (a HAL EntityModel: view fields at the top level
 * plus `_links`). Every field is optional; an absent / all-null config means
 * the network uses the service defaults. `assistantEnabled` is the *effective*
 * state, not the stored one: a network that never set it reads `true`, since
 * the assistant is on unless explicitly switched off (when off, the chat
 * endpoints answer 503). `backendLabel`, `backendEnabled`, `modelLabel` and
 * `keyConfigured` are read-only (the bring-your-own secret itself is never
 * returned).
 */
export interface AiConfigView {
    assistantEnabled?: boolean;
    backend?: string | null;
    backendLabel?: string | null;
    backendEnabled?: boolean | null;
    model?: string | null;
    modelLabel?: string | null;
    baseUrl?: string | null;
    extra?: Record<string, string> | null;
    keyConfigured?: boolean;
    mcpClients?: McpClientView[];
    _links?: HalLinks;
}

/**
 * Request body for PUT `/networks/{id}/config`. Each part is applied only when
 * present, so they are independent: `assistantEnabled` switches the assistant
 * on/off for the network (an opt-out — omit to leave it as it is); supplying
 * any of backend/model/baseUrl/extra replaces the model configuration
 * wholesale; `apiKey` is write-only and stored/rotated only when supplied (omit
 * to leave the existing secret unchanged); `mcpClients` sets the outbound MCP
 * client selection as a `key → enabled` map (false disables a server,
 * true/omitted enables it). Unknown model / backend / MCP keys are rejected
 * server-side.
 */
export interface UpdateAiConfig {
    assistantEnabled?: boolean;
    backend?: string | null;
    model?: string | null;
    baseUrl?: string | null;
    apiKey?: string | null;
    extra?: Record<string, string> | null;
    mcpClients?: Record<string, boolean>;
}

/**
 * GET `.../allowed-models` — the models a network may select, as one
 * self-describing group per backend (backend key + human-readable label +
 * its models). Supplies everything the selection UI needs, backend labels
 * included.
 */
export interface AllowedModelsView {
    allowedModels: BackendModelsView[];
    _links?: HalLinks;
}

/**
 * GET `.../allowed-mcp-clients` — the configured outbound MCP client servers a
 * network may enable/disable, each with its key, human-readable name, and
 * default (enabled) state.
 */
export interface AllowedMcpClientsView {
    clients: McpClientView[];
    _links?: HalLinks;
}

@Injectable({ providedIn: 'root' })
export class AiConfigService {
    /**
     * Effective on/off state for the last scope read or written, so the
     * floating chat launcher can gate on it without a fetch of its own. Cached
     * here rather than in a component because every read and write of the config
     * passes through this service.
     *
     * `null` (unknown) is not "enabled" — the launcher stays hidden until the
     * service has answered, so an environment where the assistant service is
     * unreachable, or has that assistant switched off entirely (its
     * endpoints 404), doesn't offer a chat that cannot work.
     */
    private readonly _enabled = signal<{ key: string; enabled: boolean } | null>(null);

    constructor(@Inject(HTTP_CLIENT) private http: HttpClient, @Inject(ENVIRONMENT) private environment: Environment) {}

    /** The effective state for `scope`, or null while unknown. */
    enabledFor(scope: AssistantScope): boolean | null {
        const cached = this._enabled();
        return cached && cached.key === assistantScopeKey(scope) ? cached.enabled : null;
    }

    get(scope: AssistantScope): Observable<AiConfigView> {
        return this.http.get<AiConfigView>(this.baseUrl(scope)).pipe(this.cacheEnabled(scope));
    }

    put(scope: AssistantScope, body: UpdateAiConfig): Observable<AiConfigView> {
        return this.http.put<AiConfigView>(this.baseUrl(scope), body).pipe(this.cacheEnabled(scope));
    }

    /**
     * Reset the scope to the service default assistant — clears the custom
     * model configuration, deletes any stored bring-your-own key, and re-enables
     * every outbound MCP client. A scope with the assistant switched off stays
     * switched off: DELETE means "revert my model configuration", not "turn the
     * assistant back on".
     */
    reset(scope: AssistantScope): Observable<void> {
        return this.http.delete<void>(this.baseUrl(scope));
    }

    allowedModels(scope: AssistantScope): Observable<AllowedModelsView> {
        return this.http.get<AllowedModelsView>(`${this.baseUrl(scope)}/allowed-models`);
    }

    allowedMcpClients(scope: AssistantScope): Observable<AllowedMcpClientsView> {
        return this.http.get<AllowedMcpClientsView>(`${this.baseUrl(scope)}/allowed-mcp-clients`);
    }

    private baseUrl(scope: AssistantScope): string {
        return `${assistantScopeUrl(this.environment.aiAssistantUrl, scope)}/config`;
    }

    /** Cache the effective flag off any view the service returns. */
    private cacheEnabled(scope: AssistantScope): MonoTypeOperatorFunction<AiConfigView> {
        const key = assistantScopeKey(scope);
        return tap((view) => this._enabled.set({ key, enabled: view.assistantEnabled ?? true }));
    }
}
